import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/features/auth/auth-store';
import { useLogout } from '@/features/auth/use-login';
import { LanguageSwitcher } from '@/components/layout/language-switcher';

interface NavItem {
  to: string;
  /** Translation key in the `nav` namespace. */
  labelKey: string;
  end?: boolean;
  adminOnly?: boolean;
}

const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', labelKey: 'home', end: true },
  { to: '/channels', labelKey: 'channels' },
  { to: '/stats', labelKey: 'stats' },
  { to: '/accounts', labelKey: 'accounts' },
  { to: '/categories', labelKey: 'categories' },
  { to: '/brands', labelKey: 'brands' },
  { to: '/insights', labelKey: 'insights' },
  { to: '/users', labelKey: 'users', adminOnly: true },
];

export function NavBar() {
  const { t } = useTranslation('nav');
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const visibleItems = NAV_ITEMS.filter((item) => !item.adminOnly || user?.is_admin);

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-card/80 backdrop-blur-xl supports-[backdrop-filter]:bg-card/60">
      <div className="container-pc flex h-14 items-center justify-between gap-4">
        <div className="flex items-center gap-6">
          <Link
            to="/"
            className="whitespace-nowrap text-lg font-bold tracking-tight text-primary transition-colors hover:text-primary/80"
          >
            {t('appTitle')}
          </Link>
          <nav className="flex items-center gap-0.5 overflow-x-auto">
            {visibleItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'relative whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all',
                    isActive
                      ? 'bg-primary/15 text-primary'
                      : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                  )
                }
              >
                {t(item.labelKey)}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          {user && (
            <div className="hidden items-center gap-2 sm:flex">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
                {user.username.charAt(0).toUpperCase()}
              </div>
              <span className="text-sm font-medium">{user.username}</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
            aria-label={t('logoutAria')}
            className="text-muted-foreground hover:text-foreground"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">{t('logout')}</span>
          </Button>
        </div>
      </div>
    </header>
  );
}
